import React, { useState, useEffect, useCallback } from 'react';
import ccService from '../services/ccService';
import SortableResizableTable from '../components/SortableResizableTable';
import fileService from '../services/fileService';

const CoveredCallsAccountManagement = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statistics, setStatistics] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null); const [formData, setFormData] = useState({
    account_name: '',
    account_number: '',
    custodian: '',
    account_type: '',
    covered_calls: 'Yes'
  });
  const [isAccountsPanelCollapsed, setIsAccountsPanelCollapsed] = useState(false);
  const [filterText, setFilterText] = useState('');
  // Wizard state
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [uploadedFile, setUploadedFile] = useState(null); const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [processedData, setProcessedData] = useState(null);
  const [processingError, setProcessingError] = useState('');
  const [isProcessedPositionsCollapsed, setIsProcessedPositionsCollapsed] = useState(true);

  // Options data state for Step 3
  const [optionsData, setOptionsData] = useState({
    sellBy: 'SCO',
    symbol: '',
    expirationDate: '',
    strikePrice: ''
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState('');

  // Historical file selection state
  const [historicalFiles, setHistoricalFiles] = useState([]);
  const [selectedHistoricalFile, setSelectedHistoricalFile] = useState('');
  const [isLoadingHistoricalFiles, setIsLoadingHistoricalFiles] = useState(false);
  const [isRerunningVariance, setIsRerunningVariance] = useState(false);

  // Define columns for the accounts table
  const accountTableColumns = [
    {
      key: 'Account Name',
      label: 'Account Name',
      description: 'Name of the account',
      sortable: true,
      filterable: false, // Global filter handles this
      initialWidth: 200
    },
    {
      key: 'Account Number',
      label: 'Account Number',
      description: 'Account identification number',
      sortable: true,
      filterable: false, // Global filter handles this
      initialWidth: 150
    },
    {
      key: 'Custodian',
      label: 'Custodian',
      description: 'Financial institution holding the account',
      sortable: true,
      filterable: true, // Column filter
      initialWidth: 120
    },
    {
      key: 'Account Type',
      label: 'Account Type',
      description: 'Type of account (Cash/Margin)',
      sortable: true,
      filterable: true, // Column filter
      initialWidth: 120
    },
    {
      key: 'Covered Calls',
      label: 'Covered Calls',
      description: 'Whether covered calls are enabled for this account',
      sortable: true,
      filterable: true, // Column filter
      initialWidth: 130
    },
    {
      key: 'Actions',
      label: 'Actions',
      description: 'Edit or delete account',
      sortable: false,
      filterable: false, // Actions don't need filtering
      initialWidth: 120
    }
  ];

  // Custom render functions for specific columns
  const customRenderFunctions = {
    'Covered Calls': (value) => (
      <span className={`badge ${value?.toLowerCase() === 'yes' ? 'bg-success' : 'bg-secondary'}`}>
        {value}
      </span>
    ),
    'Actions': (value, account) => (
      <div>
        <button
          className="btn btn-sm btn-outline-primary me-2"
          onClick={() => handleEdit(account)}
          disabled={loading}
        >
          <i className="bi bi-pencil"></i>
        </button>
        <button
          className="btn btn-sm btn-outline-danger"
          onClick={() => handleDelete(account['Account Number'])}
          disabled={loading}
        >
          <i className="bi bi-trash"></i>
        </button>
      </div>)
  };

  // Filter accounts based on search text (only Account Name and Account Number)
  const filteredAccounts = accounts.filter(account => {
    if (!filterText) return true;

    const searchText = filterText.toLowerCase();
    return (
      (account['Account Name'] || '').toLowerCase().includes(searchText) ||
      (account['Account Number'] || '').toLowerCase().includes(searchText)
    );
  });

  useEffect(() => {
    fetchAccounts();
    fetchStatistics();
    loadOptionsConfig();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const data = await ccService.getAccounts();
      setAccounts(data.accounts || []);
      setError('');
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load accounts: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const data = await ccService.getStatistics();
      setStatistics(data.statistics);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const loadOptionsConfig = async () => {
    try {
      const data = await ccService.getOptionsConfig();
      if (data.success) {
        setOptionsData(data.config);
      }
    } catch (err) {
      console.error('Error loading options config:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEditing && selectedAccount) {
        await ccService.updateAccount(selectedAccount['Account Number'], formData);
      } else {
        await ccService.createAccount(formData);
      }
      await fetchAccounts();
      await fetchStatistics();
      resetForm();
      setShowModal(false);
      setError('');
    } catch (err) {
      console.error('Error saving account:', err);
      setError('Failed to save account: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (account) => {
    setSelectedAccount(account);
    setFormData({
      account_name: account['Account Name'] || '',
      account_number: account['Account Number'] || '',
      custodian: account['Custodian'] || '',
      account_type: account['Account Type'] || '',
      covered_calls: account['Covered Calls'] || 'Yes'
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDelete = async (accountNumber) => {
    if (!window.confirm(`Are you sure you want to delete account ${accountNumber}?`)) {
      return;
    }
    try {
      setLoading(true);
      await ccService.deleteAccount(accountNumber);
      await fetchAccounts();
      await fetchStatistics();
      setError('');
    } catch (err) {
      console.error('Error deleting account:', err);
      setError('Failed to delete account: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      account_name: '',
      account_number: '',
      custodian: '',
      account_type: '',
      covered_calls: 'Yes'
    });
    setSelectedAccount(null);
    setIsEditing(false);
  };

  const handleAddNew = () => {
    resetForm();
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  // Wizard functions
  const handleGenerateCoveredCalls = () => {
    setShowWizard(true);
    setWizardStep(1);
    setUploadedFile(null);
    loadOptionsConfig(); // Load saved configuration
  };

  const handleCloseWizard = async () => {
    try {
      await ccService.cleanupVariance();
    } catch (err) {
      console.warn('Failed to cleanup variance files:', err);
    }
    setShowWizard(false);
    setWizardStep(1);
    setUploadedFile(null);
    setIsProcessingFile(false);
    setProcessedData(null);
    setProcessingError('');
    setGenerationError('');
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleNextStep = () => {
    if (wizardStep === 1 && uploadedFile) {
      // Process file when moving from step 1 to step 2
      processUploadedFile();
    } else if (wizardStep < 3) {
      setWizardStep(wizardStep + 1);
    }
  };

  const processUploadedFile = async () => {
    setIsProcessingFile(true);
    setProcessingError('');
    try {
      const responseData = await ccService.processFile(uploadedFile);
      const processedData = {
        summary: {
          totalPositions: responseData.summary.total_positions,
          eligiblePositions: responseData.summary.eligible_positions,
          totalContracts: responseData.summary.total_contracts,
          exportPath: responseData.summary.export_path,
          varianceExportPath: responseData.summary.variance_export_path
        },
        warnings: responseData.warnings || { missing_accounts: [], custodian_mismatches: [] },
        positions: responseData.positions.map(pos => ({
          accountName: pos['Account Name'],
          accountNumber: pos['Account Number'],
          symbol: pos['Symbol'],
          custodian: pos['Custodian Selection'],
          accountType: pos['Account Type'],
          quantity: pos['Quantity'],
          roundDown: pos['Round Down'],
          contractsToSell: pos['Estimate of Contracts to Sell']
        })),
        varianceAnalysis: responseData.variance_analysis ? responseData.variance_analysis.map(pos => ({
          ...pos,
          variance: pos['Variance']
        })) : null,
        hasVariance: responseData.has_variance
      };
      setProcessedData(processedData);
      setWizardStep(2);
    } catch (err) {
      console.error('Error processing file:', err);
      setProcessingError('Failed to process file: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsProcessingFile(false);
    }
  };

  const handlePreviousStep = () => {
    if (wizardStep > 1) {
      setWizardStep(wizardStep - 1);
    }
  };

  const handleGenerateCalls = async () => {
    setIsGenerating(true);
    setGenerationError('');
    try {
      const result = await ccService.generateCoveredCalls(optionsData);
      if (result.success) {
        // Create download links for both files
        const downloadFile = async (filename, downloadName) => {
          try {
            const response = await fileService.downloadFile(filename);
            
            // Check if we got a presigned URL (redirected response)
            if (response.redirected && response.url) {
              // Fetch the actual file content from the presigned URL
              const fileResponse = await fetch(response.url);
              if (!fileResponse.ok) throw new Error('Failed to download file from presigned URL');
              const blob = await fileResponse.blob();
              
              // Create download link
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = downloadName;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
            } else {
              throw new Error('Unexpected response format');
            }
          } catch (downloadError) {
            console.error('Download error:', downloadError);
            throw downloadError;
          }
        };
        
        if (result.files?.schwab) {
          await downloadFile(result.files.schwab, 'covered_calls_schwab.csv');
        }
        if (result.files?.fidelity) {
          await downloadFile(result.files.fidelity, 'covered_calls_fidelity.csv');
        }
        alert('Covered calls files generated and downloaded successfully!');
        handleCloseWizard();
      }
    } catch (err) {
      console.error('Error generating covered calls:', err);
      setGenerationError('Failed to generate covered calls: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsGenerating(false);
    }
  };

  // Function to load available historical files
  const loadHistoricalFiles = useCallback(async () => {
    if (!processedData?.hasVariance) return;
    setIsLoadingHistoricalFiles(true);
    try {
      const data = await ccService.getHistoricalFiles();
      setHistoricalFiles(data.files || []);
      if (data.files && data.files.length > 0) {
        setSelectedHistoricalFile(data.latest || data.files[0].filename);
      }
    } catch (error) {
      console.error('Error loading historical files:', error);
    } finally {
      setIsLoadingHistoricalFiles(false);
    }
  }, [processedData?.hasVariance]);

  // Function to rerun variance analysis with selected historical file
  const rerunVarianceAnalysis = async () => {
    if (!selectedHistoricalFile) return;
    setIsRerunningVariance(true);
    setProcessingError('');
    try {
      const responseData = await ccService.rerunVariance(selectedHistoricalFile);
      setProcessedData(prevData => ({
        ...prevData,
        varianceAnalysis: responseData.variance_analysis ? responseData.variance_analysis.map(pos => ({
          ...pos,
          variance: pos['Variance']
        })) : null,
        hasVariance: responseData.has_variance
      }));
    } catch (error) {
      console.error('Error rerunning variance analysis:', error);
      setProcessingError('Failed to rerun variance analysis: ' + error.message);
    } finally {
      setIsRerunningVariance(false);
    }
  };

  // Load historical files when processedData becomes available and has variance capability
  useEffect(() => {
    if (processedData?.hasVariance) {
      loadHistoricalFiles();
    }
  }, [processedData?.hasVariance, loadHistoricalFiles]);

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Covered Calls Account Management</h2>
        <button
          className="btn btn-primary"
          onClick={handleAddNew}
          disabled={loading}
        >
          <i className="bi bi-plus-circle me-2"></i>
          Add New Account
        </button>
      </div>

      {error && (
        <div className="alert alert-danger mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}
      {/* Statistics Cards */}
      {statistics && (
        <div className="row mb-2">
          <div className="col-md-3">
            <div className="card bg-primary text-white">
              <div className="card-body p-2 d-flex justify-content-between align-items-center">
                <span className="small fw-bold">Total Accounts:</span>
                <span className="h6 mb-0">{statistics.total_accounts}</span>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-success text-white">
              <div className="card-body p-2 d-flex justify-content-between align-items-center">
                <span className="small fw-bold">Covered Calls:</span>
                <span className="h6 mb-0">{statistics.covered_calls_enabled} <small>({statistics.covered_calls_percentage}%)</small></span>
              </div>
            </div>
          </div>          <div className="col-md-3">
            <div className="card bg-info text-white">
              <div className="card-body p-2 d-flex justify-content-between align-items-center">
                <span className="small fw-bold">Custodians:</span>
                <span className="small">
                  {Object.entries(statistics.custodians).map(([custodian, count], index) => (
                    <span key={custodian}>
                      {custodian}: {count}
                      {index < Object.entries(statistics.custodians).length - 1 && ', '}
                    </span>
                  ))}
                </span>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-warning text-dark">
              <div className="card-body p-2 d-flex justify-content-between align-items-center">
                <span className="small fw-bold">Account Types:</span>
                <span className="small">
                  {Object.entries(statistics.account_types).map(([type, count], index) => (
                    <span key={type}>
                      {type}: {count}
                      {index < Object.entries(statistics.account_types).length - 1 && ', '}
                    </span>
                  ))}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Accounts Table */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Accounts</h5>          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setIsAccountsPanelCollapsed(!isAccountsPanelCollapsed)}
            type="button"
          >
            <i className={`bi ${isAccountsPanelCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'}`}></i>
          </button>
        </div>
        {!isAccountsPanelCollapsed && (
          <div className="card-body">
            {/* Filter input */}
            <div className="mb-3">
              <div className="row">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-search"></i>
                    </span>                  <input
                      type="text"
                      className="form-control"
                      placeholder="Filter by account name or number..."
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                    {filterText && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setFilterText('')}
                        title="Clear filter"
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>
                  {filterText && (
                    <div className="form-text">
                      Showing {filteredAccounts.length} of {accounts.length} accounts
                    </div>
                  )}
                </div>
              </div>
            </div>

            {loading && !accounts.length ? (
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>) : accounts.length === 0 ? (
                <div className="text-center text-muted py-4">
                  No accounts found. Click "Add New Account" to get started.
                </div>
              ) : filteredAccounts.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="bi bi-search me-2"></i>
                  No accounts match your filter criteria.
                  <div className="mt-2">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setFilterText('')}
                    >
                      Clear Filter
                    </button>
                  </div>
                </div>) : (
              <SortableResizableTable
                columns={accountTableColumns}
                data={filteredAccounts}
                customRender={customRenderFunctions}
                maxRows={10}
                tableClassName="cc-management-table"
              />
            )}
          </div>
        )}
      </div>

      {/* Generate Covered Calls Button */}
      <div className="d-flex justify-content-center mt-4">
        <button
          className="btn btn-success btn-lg"
          onClick={handleGenerateCoveredCalls}
          disabled={loading}
        >
          <i className="bi bi-gear-fill me-2"></i>
          Generate Covered Calls
        </button>
      </div>

      {/* Modal for Add/Edit Account */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isEditing ? 'Edit Account' : 'Add New Account'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Account Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.account_name}
                      onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Account Number *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      required
                      disabled={isEditing} // Don't allow changing account number when editing
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Custodian *</label>
                    <select
                      className="form-select"
                      value={formData.custodian}
                      onChange={(e) => setFormData({ ...formData, custodian: e.target.value })}
                      required
                    >
                      <option value="">Select Custodian</option>
                      <option value="Schwab">Schwab</option>
                      <option value="Fidelity">Fidelity</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Account Type *</label>
                    <select
                      className="form-select"
                      value={formData.account_type}
                      onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                      required
                    >
                      <option value="">Select Account Type</option>
                      <option value="Cash">Cash</option>
                      <option value="Margin">Margin</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Covered Calls *</label>
                    <select
                      className="form-select"
                      value={formData.covered_calls}
                      onChange={(e) => setFormData({ ...formData, covered_calls: e.target.value })}
                      required
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {isEditing ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      isEditing ? 'Update Account' : 'Create Account'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Covered Calls Generation Wizard */}
      {showWizard && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-gear-fill me-2"></i>
                  Generate Covered Calls - Step {wizardStep} of 3
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseWizard}
                ></button>
              </div>
              <div className="modal-body">
                {/* Step Progress Indicator */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between">
                    <div className={`text-center ${wizardStep >= 1 ? 'text-primary' : 'text-muted'}`}>
                      <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${wizardStep >= 1 ? 'bg-primary text-white' : 'bg-light'}`} style={{ width: '40px', height: '40px' }}>
                        1
                      </div>
                      <div className="small mt-1">Upload File</div>
                    </div>
                    <div className={`text-center ${wizardStep >= 2 ? 'text-primary' : 'text-muted'}`}>
                      <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${wizardStep >= 2 ? 'bg-primary text-white' : 'bg-light'}`} style={{ width: '40px', height: '40px' }}>
                        2
                      </div>
                      <div className="small mt-1">Review Data</div>
                    </div>
                    <div className={`text-center ${wizardStep >= 3 ? 'text-primary' : 'text-muted'}`}>
                      <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${wizardStep >= 3 ? 'bg-primary text-white' : 'bg-light'}`} style={{ width: '40px', height: '40px' }}>
                        3
                      </div>
                      <div className="small mt-1">Generate</div>
                    </div>
                  </div>
                  <div className="progress mt-3" style={{ height: '3px' }}>
                    <div
                      className="progress-bar bg-primary"
                      role="progressbar"
                      style={{ width: `${(wizardStep / 3) * 100}%` }}
                    ></div>
                  </div>
                </div>

                {/* Step 1: File Upload */}
                {wizardStep === 1 && (
                  <div>
                    <h6 className="mb-3">Step 1: Upload Security Cross-Reference File</h6>
                    <p className="text-muted mb-4">
                      Please upload the lastest Security Cross-Reference report from Tamarac.
                    </p>

                    <div className="mb-4">
                      <label className="form-label">Select File *</label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".csv"
                        onChange={handleFileUpload}
                      />
                      <div className="form-text">
                        Supported formats: CSV
                      </div>
                    </div>

                    {uploadedFile && (
                      <div className="alert alert-success">
                        <i className="bi bi-check-circle me-2"></i>
                        File selected: {uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(1)} KB)
                      </div>
                    )}
                  </div>
                )}
                {/* Step 2: Review Processed Data */}
                {wizardStep === 2 && (
                  <div>
                    <h6 className="mb-3">Step 2: Review Covered Calls Analysis</h6>
                    <p className="text-muted mb-4">
                      Review the analysis of your uploaded file and the suggested covered calls positions.
                    </p>

                    {isProcessingFile && (
                      <div className="text-center py-4">
                        <div className="spinner-border text-primary mb-3" role="status">
                          <span className="visually-hidden">Processing file...</span>
                        </div>
                        <div>
                          <h6>Processing Security Cross-Reference File</h6>
                          <p className="text-muted">Analyzing positions and calculating covered calls recommendations...</p>
                        </div>
                      </div>
                    )}

                    {processingError && (
                      <div className="alert alert-danger">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {processingError}
                      </div>
                    )}

                    {processedData && !isProcessingFile && (
                      <div>
                        {/* Data Warnings */}
                        {processedData.warnings && (processedData.warnings.missing_accounts?.length > 0 || processedData.warnings.custodian_mismatches?.length > 0) && (
                          <div className="alert alert-warning alert-dismissible fade show mb-4" role="alert">
                            <h6 className="alert-heading">
                              <i className="bi bi-exclamation-triangle-fill me-2"></i>
                              Data Warnings Detected
                            </h6>
                            
                            {/* Missing Accounts */}
                            {processedData.warnings.missing_accounts?.length > 0 && (
                              <div className="mb-3">
                                <strong className="d-block mb-2">
                                  Missing Accounts in Taramac ({processedData.warnings.missing_accounts.length}):
                                </strong>
                                <div className="table-responsive">
                                  <table className="table table-sm table-bordered bg-white mb-2">
                                    <thead className="table-light">
                                      <tr>
                                        <th>Account Number</th>
                                        <th>Account Name</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {processedData.warnings.missing_accounts.map((account, idx) => (
                                        <tr key={idx}>
                                          <td>{account['Account Number']}</td>
                                          <td>{account['Account Name']}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                <small className="text-muted">
                                  <i className="bi bi-info-circle me-1"></i>
                                  These accounts from Covered Call Helper were not found in the Taramac Security Cross-Reference file and will be excluded from covered calls processing.
                                </small>
                              </div>
                            )}
                            
                            {/* Custodian Mismatches */}
                            {processedData.warnings.custodian_mismatches?.length > 0 && (
                              <div>
                                <strong className="d-block mb-2">
                                  Custodian Mismatches ({processedData.warnings.custodian_mismatches.length}):
                                </strong>
                                <div className="table-responsive">
                                  <table className="table table-sm table-bordered bg-white mb-2">
                                    <thead className="table-light">
                                      <tr>
                                        <th>Account Number</th>
                                        <th>Account Name</th>
                                        <th>accountInfo Custodian</th>
                                        <th>Taramac Custodian</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {processedData.warnings.custodian_mismatches.map((mismatch, idx) => (
                                        <tr key={idx}>
                                          <td>{mismatch['Account Number']}</td>
                                          <td>{mismatch['Account Name (accountInfo.csv)']}</td>
                                          <td>{mismatch['Custodian (accountInfo.csv)']}</td>
                                          <td className="fw-bold text-primary">{mismatch['Custodian (Taramac)']}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                <small className="text-muted">
                                  <i className="bi bi-info-circle me-1"></i>
                                  These accounts have different custodians between accountInfo.csv and Taramac. Processing will use the <strong>Taramac custodian</strong> value (shown in blue).
                                </small>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Summary Cards */}
                        <div className="row mb-4">
                          {/* Total Positions by Custodian */}
                          <div className="col-lg-4 col-md-6 mb-3">
                            <div className="card bg-primary text-white h-100">
                              <div className="card-body p-3 text-center">
                                <h6 className="card-title mb-2">Total Positions</h6>
                                <h4 className="mb-2">{processedData.summary.totalPositions}</h4>
                                <div className="small">
                                  <div>Schwab: {processedData.positions.filter(p => p.custodian === 'Schwab').length}</div>
                                  <div>Fidelity: {processedData.positions.filter(p => p.custodian === 'Fidelity').length}</div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Eligible Positions by Custodian */}
                          <div className="col-lg-4 col-md-6 mb-3">
                            <div className="card bg-success text-white h-100">
                              <div className="card-body p-3 text-center">
                                <h6 className="card-title mb-2">Eligible for Calls</h6>
                                <h4 className="mb-2">{processedData.summary.eligiblePositions}</h4>
                                <div className="small">
                                  <div>Schwab: {processedData.positions.filter(p => p.custodian === 'Schwab' && p.contractsToSell > 0).length}</div>
                                  <div>Fidelity: {processedData.positions.filter(p => p.custodian === 'Fidelity' && p.contractsToSell > 0).length}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          {/* Total Contracts by Custodian */}
                          <div className="col-lg-4 col-md-6 mb-3">
                            <div className="card bg-info text-white h-100">
                              <div className="card-body p-3 text-center">
                                <h6 className="card-title mb-2">Total Contracts</h6>
                                <h4 className="mb-2">{processedData.summary.totalContracts}</h4>
                                <div className="small">
                                  <div>Schwab: {processedData.positions.filter(p => p.custodian === 'Schwab').reduce((sum, p) => sum + (p.contractsToSell || 0), 0)}</div>
                                  <div>Fidelity: {processedData.positions.filter(p => p.custodian === 'Fidelity').reduce((sum, p) => sum + (p.contractsToSell || 0), 0)}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Variance Analysis */}
                        {processedData.hasVariance && processedData.varianceAnalysis && (
                          <div className="card mt-3">
                            <div className="card-header bg-info text-white">
                              <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">
                                  <i className="bi bi-graph-up me-2"></i>
                                  Variance Analysis Report
                                </h6>
                                {/* Historical File Selection */}
                                <div className="d-flex align-items-center gap-2">
                                  <label className="form-label text-white mb-0 me-2">Historical File:</label>
                                  <select
                                    className="form-select form-select-sm"
                                    style={{ width: '200px' }}
                                    value={selectedHistoricalFile}
                                    onChange={(e) => setSelectedHistoricalFile(e.target.value)}
                                    disabled={isLoadingHistoricalFiles || isRerunningVariance}
                                  >
                                    {isLoadingHistoricalFiles ? (
                                      <option>Loading...</option>
                                    ) : (
                                      historicalFiles.map((file) => (
                                        <option key={file.filename} value={file.filename}>
                                          {file.filename} ({file.modified_date})
                                        </option>
                                      ))
                                    )}
                                  </select>
                                  <button
                                    className="btn btn-light btn-sm"
                                    onClick={rerunVarianceAnalysis}
                                    disabled={!selectedHistoricalFile || isRerunningVariance || isLoadingHistoricalFiles}
                                  >
                                    {isRerunningVariance ? (
                                      <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                        Updating...
                                      </>
                                    ) : (
                                      <>
                                        <i className="bi bi-arrow-clockwise me-1"></i>
                                        Rerun
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div className="card-body">
                              <p className="text-muted mb-3">
                                Comparison with previous estimates showing percentage changes in contract recommendations.
                                {selectedHistoricalFile && (
                                  <span className="d-block mt-1">
                                    <strong>Comparing against:</strong> {selectedHistoricalFile}
                                  </span>
                                )}
                              </p>
                              <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                <table className="table table-striped table-hover table-sm">
                                  <thead className="table-dark sticky-top">
                                    <tr>
                                      <th>Account Name</th>
                                      <th>Symbol</th>
                                      <th>Current Contracts</th>
                                      <th>Previous Contracts</th>
                                      <th>Variance %</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {processedData.varianceAnalysis.map((item, index) => (
                                      <tr key={index}>
                                        <td>{item['Account Name']}</td>
                                        <td className="fw-bold">{item['Symbol']}</td>
                                        <td>{item['Estimate of Contracts to Sell']}</td>
                                        <td>{item['Previous Estimate of Contracts to Sell'] || 'N/A'}</td>
                                        <td>
                                          <span className={`badge ${item['Variance'] === 999999 ? 'bg-secondary' :
                                            item['Variance'] > 10 ? 'bg-success' :
                                              item['Variance'] < -10 ? 'bg-danger' :
                                                'bg-warning text-dark'
                                            }`}>
                                            {item['Variance'] === 999999 ? 'New' : `${item['Variance']}%`}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Processed Positions Data - Collapsible */}
                        <div className="card mt-3">
                          <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setIsProcessedPositionsCollapsed(!isProcessedPositionsCollapsed)}>
                            <h6 className="mb-0 d-flex justify-content-between align-items-center">
                              <span>
                                <i className="bi bi-table me-2"></i>
                                Processed Positions Data
                              </span>
                              <i className={`bi ${isProcessedPositionsCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'}`}></i>
                            </h6>
                          </div>
                          <div className={`collapse ${!isProcessedPositionsCollapsed ? 'show' : ''}`}>
                            <div className="card-body">
                              <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                <table className="table table-striped table-hover table-sm">
                                  <thead className="table-dark sticky-top">
                                    <tr>
                                      <th>Account Name</th>
                                      <th>Account #</th>
                                      <th>Symbol</th>
                                      <th>Custodian</th>
                                      <th>Quantity</th>
                                      <th>Round Down</th>
                                      <th>Contracts to Sell</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {processedData.positions.map((position, index) => (
                                      <tr key={index}>
                                        <td>{position.accountName}</td>
                                        <td>{position.accountNumber}</td>
                                        <td className="fw-bold">{position.symbol}</td>
                                        <td>{position.custodian}</td>
                                        <td>{position.quantity?.toLocaleString()}</td>
                                        <td>{position.roundDown?.toLocaleString()}</td>
                                        <td className="text-success fw-bold">{position.contractsToSell}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Analysis Notes */}
                        <div className="card mt-3 bg-light">
                          <div className="card-body">
                            <h6 className="card-title">
                              <i className="bi bi-info-circle me-2"></i>
                              Analysis Notes
                            </h6>
                            <ul className="mb-0">
                              <li>Positions with 100+ shares are eligible for covered calls</li>
                              <li>Round Down calculation uses 96% of total quantity rounded to nearest 100</li>
                              <li>Contracts to Sell = Round Down ÷ 100</li>
                              {processedData.hasVariance && <li>Variance shows percentage change from previous analysis (999999% indicates new position)</li>}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Step 3: Generate Options Data Form */}
                {wizardStep === 3 && (
                  <div>
                    <h6 className="mb-3">Step 3: Configure Options Parameters</h6>
                    <p className="text-muted mb-4">
                      Enter the options parameters for generating covered calls.
                    </p>

                    {generationError && (
                      <div className="alert alert-danger">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {generationError}
                      </div>
                    )}

                    <form onSubmit={(e) => { e.preventDefault(); handleGenerateCalls(); }}>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Sell By Type *</label>
                            <select
                              className="form-select"
                              value={optionsData.sellBy}
                              onChange={(e) => setOptionsData({ ...optionsData, sellBy: e.target.value })}
                              required
                            >
                              <option value="SCO">SCO (Sell Call to Open)</option>
                              <option value="BCC">BCC (Buy Call to Close)</option>
                            </select>
                            <div className="form-text">
                              Choose the type of call option transaction
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Symbol *</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="e.g., AAPL, TSLA"
                              value={optionsData.symbol}
                              onChange={(e) => setOptionsData({ ...optionsData, symbol: e.target.value.toUpperCase() })}
                              required
                            />
                            <div className="form-text">
                              Enter the underlying stock symbol
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Expiration Date *</label>
                            <input
                              type="date"
                              className="form-control"
                              value={optionsData.expirationDate}
                              onChange={(e) => setOptionsData({ ...optionsData, expirationDate: e.target.value })}
                              required
                            />
                            <div className="form-text">
                              Select the option expiration date
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Strike Price *</label>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              placeholder="e.g., 150.00"
                              value={optionsData.strikePrice}
                              onChange={(e) => setOptionsData({ ...optionsData, strikePrice: e.target.value })}
                              required
                            />
                            <div className="form-text">
                              Enter the strike price for the options
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Summary of current settings */}
                      <div className="card bg-light mt-3">
                        <div className="card-body">
                          <h6 className="card-title">
                            <i className="bi bi-gear me-2"></i>
                            Options Summary
                          </h6>
                          <div className="row">
                            <div className="col-md-3">
                              <strong>Type:</strong> {optionsData.sellBy}
                            </div>
                            <div className="col-md-3">
                              <strong>Symbol:</strong> {optionsData.symbol || 'Not set'}
                            </div>                            <div className="col-md-3">
                              <strong>Expiration:</strong> {optionsData.expirationDate ?
                                (() => {
                                  const date = new Date(optionsData.expirationDate);
                                  const year = date.getFullYear().toString().slice(-2);
                                  const month = (date.getMonth() + 1).toString().padStart(2, '0');
                                  const day = date.getDate().toString().padStart(2, '0');
                                  return `${year}-${month}-${day}`;
                                })() : 'Not set'
                              }
                            </div>
                            <div className="col-md-3">
                              <strong>Strike:</strong> ${optionsData.strikePrice || 'Not set'}
                            </div>
                          </div>
                          {optionsData.symbol && optionsData.expirationDate && optionsData.strikePrice && (
                            <div className="mt-3 p-3 bg-white rounded border">
                              <strong>Option Symbol:</strong>
                              <span className="ms-2 text-primary fw-bold">
                                {(() => {
                                  const date = new Date(optionsData.expirationDate);
                                  const year = date.getFullYear().toString().slice(-2);
                                  const month = (date.getMonth() + 1).toString().padStart(2, '0');
                                  const day = date.getDate().toString().padStart(2, '0');
                                  const strike = parseFloat(optionsData.strikePrice).toFixed(2).replace('.', '');
                                  return `${optionsData.symbol}${year}${month}${day}C${strike}`;
                                })()}
                              </span>
                            </div>
                          )}
                          <div className="mt-2">
                            <small className="text-muted">
                              These settings will be saved and used as defaults for future generations.
                            </small>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseWizard}
                >
                  Cancel
                </button>
                {wizardStep > 1 && (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handlePreviousStep}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Previous
                  </button>
                )}
                {wizardStep < 3 && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleNextStep}
                    disabled={(wizardStep === 1 && !uploadedFile) || isProcessingFile}
                  >
                    {isProcessingFile ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        Next
                        <i className="bi bi-arrow-right ms-2"></i>
                      </>
                    )}
                  </button>
                )}
                {wizardStep === 3 && (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleGenerateCalls}
                    disabled={isGenerating || !optionsData.sellBy || !optionsData.symbol || !optionsData.expirationDate || !optionsData.strikePrice}
                  >
                    {isGenerating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Generating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-gear-fill me-2"></i>
                        Generate Covered Calls
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoveredCallsAccountManagement;
